const ALLOWED = new Set(["p", "br", "h1", "h2", "h3", "h4", "strong", "b", "em", "i", "ul", "ol", "li", "a"]);
const ALLOWED_A_ATTRS = new Set(["href", "title", "rel", "target"]);

export function preserveImportedDescription(raw: string | null | undefined): string {
  return String(raw ?? "");
}

function isSafeHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (/^(javascript|data|vbscript):/i.test(h)) return false;
  return /^(https?:|mailto:|\/|#)/i.test(h);
}

function stripMsoJunk(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
    .replace(/\sclass="?Mso[A-Za-z0-9]+"?/gi, "")
    .replace(/\sstyle="[^"]*mso-[^"]*"/gi, "")
    .replace(/\sdata-(?:start|end)="[^"]*"/gi, "")
    .replace(/<\/?font\b[^>]*>/gi, "")
    .replace(/<\/?span\b[^>]*>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/href="javascript:[^"]*"/gi, 'href="#"');
}

function cleanElement(el: Element): Node | null {
  const tag = el.tagName.toLowerCase();
  if (tag === "script" || tag === "iframe" || tag === "style" || tag === "object" || tag === "embed") {
    return null;
  }
  if (!ALLOWED.has(tag)) {
    const frag = el.ownerDocument.createDocumentFragment();
    for (const child of Array.from(el.childNodes)) {
      const cleaned = cleanNode(child);
      if (cleaned) frag.appendChild(cleaned);
    }
    return frag;
  }
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (tag === "a" && ALLOWED_A_ATTRS.has(name)) {
      if (name === "href" && !isSafeHref(attr.value)) el.removeAttribute(attr.name);
      continue;
    }
    el.removeAttribute(attr.name);
  }
  if (tag === "a") {
    el.setAttribute("rel", "noopener noreferrer");
    el.setAttribute("target", "_blank");
  }
  const children = Array.from(el.childNodes);
  while (el.firstChild) el.removeChild(el.firstChild);
  for (const child of children) {
    const cleaned = cleanNode(child);
    if (cleaned) el.appendChild(cleaned);
  }
  return el;
}

function cleanNode(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) return node;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  return cleanElement(node as Element);
}

/** Safe HTML for Tradivo rendering. Original markup is not mutated. */
export function sanitizeCatalogueHtml(raw: string | null | undefined): string {
  const original = preserveImportedDescription(raw);
  if (!original.trim()) return "";
  const stripped = stripMsoJunk(original);
  if (typeof document === "undefined") {
    return stripped
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<iframe[\s\S]*?<\/iframe>/gi, "");
  }
  const wrap = document.createElement("div");
  wrap.innerHTML = stripped;
  const out = document.createElement("div");
  for (const child of Array.from(wrap.childNodes)) {
    const cleaned = cleanNode(child);
    if (cleaned) out.appendChild(cleaned);
  }
  const html = out.innerHTML.trim();
  if (html) return html;
  const text = wrap.textContent?.replace(/\s+\n/g, "\n").trim() ?? "";
  return text ? `<p>${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>` : "";
}

export function catalogueDescriptionIsUnsafe(raw: string | null | undefined): boolean {
  const s = String(raw ?? "");
  return /<(script|iframe|style)\b/i.test(s) || /\son\w+=/i.test(s) || /javascript:/i.test(s);
}
